import type { VolumeProfileSnapshot } from '../snapshot';
import type {
    VolumeProfileIncrement,
    VolumeProfileOverlay,
    VolumeProfileRange,
    VolumeProfileSession,
    VolumeProfileSubject,
    VolumeProfileTabId,
    VolumeProfileViewMode,
} from './types';
import type { KiasiHistorySummaryRow } from 'src/lib/kiasi';
import type { VolumeBarSnapshot } from 'src/lib/kiasi/bars';
import type { LiveBarProfile, LiveBarStatus } from 'src/services/KiasiLiveBarService';

export type SetTabAction = { type: 'SET_TAB'; tabId: VolumeProfileTabId };
export const setTab = (tabId: VolumeProfileTabId): SetTabAction => ({ type: 'SET_TAB', tabId });

export type SetViewModeAction = { type: 'SET_VIEW_MODE'; viewMode: VolumeProfileViewMode };
export const setViewMode = (viewMode: VolumeProfileViewMode): SetViewModeAction => ({
    type: 'SET_VIEW_MODE',
    viewMode,
});

export type SetSessionAction = { type: 'SET_SESSION'; session: VolumeProfileSession };
export const setSession = (session: VolumeProfileSession): SetSessionAction => ({ type: 'SET_SESSION', session });

export type SetRangeAction = { type: 'SET_RANGE'; range: VolumeProfileRange };
export const setRange = (range: VolumeProfileRange): SetRangeAction => ({ type: 'SET_RANGE', range });

export type SetIncrementAction = { type: 'SET_INCREMENT'; increment: VolumeProfileIncrement };
export const setIncrement = (increment: VolumeProfileIncrement): SetIncrementAction => ({
    type: 'SET_INCREMENT',
    increment,
});

export type ToggleOverlayAction = { type: 'TOGGLE_OVERLAY'; overlay: VolumeProfileOverlay };
export const toggleOverlay = (overlay: VolumeProfileOverlay): ToggleOverlayAction => ({
    type: 'TOGGLE_OVERLAY',
    overlay,
});

export type SubjectChangedAction = { type: 'SUBJECT_CHANGED'; subject: VolumeProfileSubject | null };
export const subjectChanged = (subject: VolumeProfileSubject | null): SubjectChangedAction => ({
    type: 'SUBJECT_CHANGED',
    subject,
});

export type HistoryLoadingAction = { type: 'HISTORY_LOADING' };
export const historyLoading = (): HistoryLoadingAction => ({ type: 'HISTORY_LOADING' });

export type HistoryLoadedAction = { type: 'HISTORY_LOADED'; snapshot: VolumeProfileSnapshot };
export const historyLoaded = (snapshot: VolumeProfileSnapshot): HistoryLoadedAction => ({
    type: 'HISTORY_LOADED',
    snapshot,
});

export type HistoryFailedAction = { type: 'HISTORY_FAILED'; error: Error };
export const historyFailed = (error: Error): HistoryFailedAction => ({ type: 'HISTORY_FAILED', error });

/** `null` when the summary query has no data (not loaded yet, or reset). */
export type SummaryLoadedAction = { type: 'SUMMARY_LOADED'; rows: readonly KiasiHistorySummaryRow[] | null };
export const summaryLoaded = (rows: readonly KiasiHistorySummaryRow[] | null): SummaryLoadedAction => ({
    type: 'SUMMARY_LOADED',
    rows,
});

export type LiveBarUpdatedAction = { type: 'LIVE_BAR_UPDATED'; bar: VolumeBarSnapshot | null };
export const liveBarUpdated = (bar: VolumeBarSnapshot | null): LiveBarUpdatedAction => ({
    type: 'LIVE_BAR_UPDATED',
    bar,
});

export type LiveStatusChangedAction = { type: 'LIVE_STATUS_CHANGED'; status: LiveBarStatus };
export const liveStatusChanged = (status: LiveBarStatus): LiveStatusChangedAction => ({
    type: 'LIVE_STATUS_CHANGED',
    status,
});

export type LiveProfileChangedAction = { type: 'LIVE_PROFILE_CHANGED'; profile: LiveBarProfile | null };
export const liveProfileChanged = (profile: LiveBarProfile | null): LiveProfileChangedAction => ({
    type: 'LIVE_PROFILE_CHANGED',
    profile,
});

export type VolumeProfileAction =
    | SetTabAction
    | SetViewModeAction
    | SetSessionAction
    | SetRangeAction
    | SetIncrementAction
    | ToggleOverlayAction
    | SubjectChangedAction
    | HistoryLoadingAction
    | HistoryLoadedAction
    | HistoryFailedAction
    | SummaryLoadedAction
    | LiveBarUpdatedAction
    | LiveStatusChangedAction
    | LiveProfileChangedAction;
